import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Fact, Project } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Hero } from '@/components/landing/hero'
import { Manifesto } from '@/components/landing/manifesto'
import { Gifts } from '@/components/landing/gifts'
import { Facts } from '@/components/landing/facts'
import { Projects } from '@/components/landing/projects'
import { Brochure } from '@/components/landing/brochure'
import { Join } from '@/components/landing/join'
import { getFacts, getProjects } from '@/lib/content'

interface HomeProps {
    projects: Array<Project>
    facts: Array<Fact>
}

// Defaults cover renders without page props, such as a hot reload of a page that had none before.
const Home: FC<Partial<HomeProps>> = ({ projects = [], facts = [] }): ReactNode => {
    return (
        <Layout>
            <Seo />

            <Hero />
            <Manifesto />
            <Gifts />
            <Facts facts={facts} />
            <Projects projects={projects} />
            <Brochure />
            <Join />
        </Layout>
    )
}

// Projects and facts come from the CMS and refresh every minute on the live site.
export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    try {
        const [projects, facts] = await Promise.all([getProjects(), getFacts()])

        return { props: { projects, facts }, revalidate: 60 }
    }
    catch (error) {
        console.error('Could not load the CMS content:', error instanceof Error ? error.message : error)

        return { props: { projects: [], facts: [] }, revalidate: 30 }
    }
}

export default Home
